import React, { useState, useEffect, useMemo, useCallback } from "react";
import Select from "react-select";
import TextareaAutosize from "react-textarea-autosize";
import "react-datepicker/dist/react-datepicker.css";
import { useFilter } from "../contexts/FilterContext";
import DatePickerPopup from "./DatePickerPopup.js";
import QueueFilter from './QueueFilter';

function MainFilter({
  updateShowTrends,
  categories,
  intents,
  agents,
  initiationMethods,
  queues,
  setFilters,
  showTrends, 
}) {
  const { filters } = useFilter();
  const [callIdInput, setCallIdInput] = useState(filters.callId || "");
  const [localDateStart, setLocalDateStart] = useState(new Date());
  const [localDateEnd, setLocalDateEnd] = useState(new Date());
  const [showDatePickerPopup, setShowDatePickerPopup] = useState(false);

  const dateOptions = useMemo(
    () => [
      { value: "today", label: "Today" },
      { value: "thisWeek", label: "This Week" },
      { value: "thisMonth", label: "This Month" },
      { value: "thisYear", label: "This Year" },
      { value: "all", label: "All" },
      { value: "custom", label: "Custom" },
    ],
    []
  );

  const findOptionByValue = (value) => {
    return agents.find((agent) => agent.value === value) || null;
  };

  const formatDate = (date) => {
    const d = new Date(date);
    return d.toISOString().split("T")[0];
  };

  const handleChange = useCallback(
    (selectedOptions, { name }) => {
      if (name === "dateRange") {
        if (selectedOptions.value === "custom") {
          setShowDatePickerPopup(true);
        } else {
          setFilters((prev) => ({
            ...prev,
            dateRange: selectedOptions,
            dateStart: null,
            dateEnd: null,
          }));
        }
      } else {
        setFilters((prev = {}) => ({
          ...prev,
          [name]: selectedOptions,
        }));
      }
    },
    [setFilters]
  );

  useEffect(() => {
    const handler = setTimeout(() => {
      setFilters((prev = {}) => {
        if (prev.callId === callIdInput) {
          return prev; // No change, avoid re-render
        }
        return { ...prev, callId: callIdInput.trim() };
      });
    }, 300);

    return () => {
      clearTimeout(handler);
    };
  }, [callIdInput, setFilters]);

  const clearFilter = useCallback(
    (name) => {
      setFilters((prev = {}) => {
        const newFilters = {
          ...prev,
          [name]: name === "callId" ? "" : name === "agent" ? null : [],
        };
        return newFilters;
      });
      if (name === "callId") {
        setCallIdInput("");
      }
    },
    [setFilters]
  );

  const customStyles = {
    control: (provided, state) => ({
      ...provided,
      minHeight: "38px",
      height: "auto",
      backgroundColor:
        state.selectProps.name === "dateRange" &&
        filters.dateRange &&
        filters.dateRange.value === "custom"
          ? "#e6f7ff" // Highlight when custom date range is selected
          : "white",
    }),
    option: (provided) => ({
      ...provided,
      display: "flex",
      justifyContent: "space-between",
    }),
    multiValue: (provided) => ({
      ...provided,
      backgroundColor: "#e2e8f0",
    }),
    multiValueLabel: (provided) => ({
      ...provided,
      fontSize: "14px",
    }),
    valueContainer: (provided) => ({
        ...provided,
        flexWrap: "wrap",
        overflowY: "auto",
        padding: "2px 6px",
    }),
    placeholder: (provided) => ({
        ...provided,
        whiteSpace: "nowrap",
    }),
    menu: (provided) => ({
      ...provided,
      zIndex: 1000, // Increase this value as needed
      width: "auto",
      minWidth: "355px", // Same as control width
      maxWidth: "550px", // 2x the control width
      left: "50%",
      transform: "translateX(-50%)",
    }),
  };

  const formatOptionLabel = ({ label, count }, { context }) => {
    if (context === "menu") {
      return (
        <div style={{ display: "flex", justifyContent: "space-between" }}>
          <span>{label}</span>
          {count !== undefined && (
            <span style={{ color: "#aaa", paddingLeft: "5px" }}>({count})</span>
          )}
        </div>
      );
    } else {
      return label;
    }
  };

  const handleExcludeWeekends = (event) => {
    setFilters((prev) => ({ ...prev, excludeWeekends: event.target.checked }));
  };

  const handleShowTrends = () => {
    updateShowTrends(!showTrends);
  };

  useEffect(() => {
    if (filters.dateStart) {
      setLocalDateStart(new Date(filters.dateStart));
    }
    if (filters.dateEnd) {
      setLocalDateEnd(new Date(filters.dateEnd));
    }
  }, [filters.dateStart, filters.dateEnd]);

  // Handle date picker confirmation
  const handleDatePickerConfirm = (startDate, endDate) => {
    setFilters((prev) => ({
      ...prev,
      dateStart: startDate,
      dateEnd: endDate,
      dateRange: {
        value: "custom",
        label: `${formatDate(startDate)} : ${formatDate(endDate)}`,
      },
    }));
    setShowDatePickerPopup(false);
  };

  return (
    <div className="mb-4 p-4 rounded-lg">
      <div className="flex space-x-4 mb-4">
        <div className="flex-1 relative">
          <TextareaAutosize
            name="callId"
            value={callIdInput}
            onChange={(e) => setCallIdInput(e.target.value)}
            placeholder="Contact ID"
            className="w-full p-2 rounded resize-none"
            minRows={1}
            maxRows={3}
          />
          {callIdInput && (
            <button
              onClick={() => clearFilter("callId")}
              className="absolute right-2 top-2 text-gray-500 hover:text-gray-700"
            >
              ✕
            </button>
          )}
        </div>
        <div className="flex-1 relative">
          <Select
            name="agent"
            value={
              filters.agent ? findOptionByValue(filters.agent.value) : null
            }
            onChange={(option) => handleChange(option, { name: "agent" })}
            options={agents}
            isClearable
            placeholder="Select Agent"
            styles={customStyles}
            formatOptionLabel={formatOptionLabel}
            theme={(theme) => ({
              ...theme,
              colors: {
                ...theme.colors,
                primary: "rgb(59, 130, 246)", // Tailwind blue-500 color
              },
            })}
          />
        </div>
        <div className="flex-1 relative">
          <Select
            name="category"
            value={filters.category}
            onChange={(option) => handleChange(option, { name: "category" })}
            options={categories}
            isClearable
            placeholder="Select Category"
            styles={customStyles}
            formatOptionLabel={formatOptionLabel}
            theme={(theme) => ({
              ...theme,
              colors: {
                ...theme.colors,
                primary: "rgb(59, 130, 246)", // Tailwind blue-500 color
              },
            })}
          />
        </div>
        <div className="flex-1 relative">
          <Select
            name="intent"
            value={filters.intent}
            onChange={(option) => handleChange(option, { name: "intent" })}
            options={intents}
            isClearable
            placeholder="Select Intent"
            styles={customStyles}
            formatOptionLabel={formatOptionLabel}
            theme={(theme) => ({
              ...theme,
              colors: {
                ...theme.colors,
                primary: "rgb(59, 130, 246)", // Tailwind blue-500 color
              },
            })}
          />
        </div>
      </div>
      <div className="flex justify-end items-center space-x-4 mt-0">
	        <div className="flex-1 relative">
        <Select
          name="dateRange"
          value={
            filters.dateRange &&
            filters.dateRange.value === "custom" &&
            filters.dateStart &&
            filters.dateEnd
              ? {
                  value: "custom",
                  label: `Custom - ${formatDate(filters.dateStart)} : ${formatDate(
                    filters.dateEnd
                  )}`,
                }
              : filters.dateRange
          }
          onChange={(option) => handleChange(option, { name: "dateRange" })}
          options={dateOptions}
          styles={customStyles}
          theme={(theme) => ({
            ...theme,
            colors: {
              ...theme.colors,
              primary: "rgb(56 126 252)",
            },
          })}
        />
      </div>
        <div className="flex-1 relative">
            <QueueFilter
                queues={queues}
                selectedQueues={filters.queue || []}
                onChange={(selectedQueues) =>
                    setFilters((prev = {}) => ({
                        ...prev,
                        queue: selectedQueues,
                    }))
                }
                placeholder="Select Queue"
            />
        </div>
        <div className="flex-1 relative">
          <Select
            name="initiationMethod"
            value={filters.initiationMethod}
            onChange={(option) =>
              handleChange(option, { name: "initiationMethod" })
            }
            options={initiationMethods}
            isClearable
            isMulti
            placeholder="Select Contact Origination"
            styles={customStyles}
            formatOptionLabel={formatOptionLabel}
            theme={(theme) => ({
              ...theme,
              colors: {
                ...theme.colors,
                primary: "rgb(56 126 252)",
              },
            })}
          />
        </div>
        <div className="flex-1 relative flex justify-center items-center">
          <div className="flex space-x-8">
            <label className={`flex items-center space-x-2 ${filters.dateRange.value === "today" ? "invisible" : ""}`}>
              <input
                type="checkbox"
                checked={filters.excludeWeekends}
                onChange={handleExcludeWeekends}
                className="form-checkbox h-5 w-5 text-usan-primary"
              />
              <span className="text-sm">Exclude weekends</span>
            </label>
            <button
              onClick={handleShowTrends}
              className="px-4 py-1 usan-primary text-white rounded hover:bg-blue-600 text-sm"
            >
              {showTrends ? "Hide Trends" : "Show Trends"}
            </button>
          </div>
        </div>
      </div>

      {/* Date Picker Popup */}
      <DatePickerPopup
        isOpen={showDatePickerPopup}
        onClose={() => setShowDatePickerPopup(false)}
        onConfirm={handleDatePickerConfirm}
        startDate={localDateStart}
        endDate={localDateEnd}
        onStartDateChange={setLocalDateStart}
        onEndDateChange={setLocalDateEnd}
      />
    </div>
  );
}

export default MainFilter;
